import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { Cta, CtaProps } from "../../buttons/cta";
import { Highlights } from "../../highlights";
import { RichText } from "../../rich-text";
import { BannerGridImage } from "./banner-grid-image";
import { BannerGridColorSchemes } from "./types";
import { BannerGridOrientation } from "./types";

export interface BannerGridProps extends Queries.BannerGridDataFragment {}

export const BannerGrid: FC<BannerGridProps> = ({
  title,
  abstract,
  image,
  enableImageZoom,
  imageFit,
  colorScheme,
  backgroundImage,
  desktopLayout,
  spacing,
  ctas,
  highlights,
}) => {
  // Determine whether CTA variant to use by Banner Grid variant (including bg image)
  const getCtaVariant = (index: number): CtaProps["variant"] => {
    if (!!backgroundImage) {
      return index === 0 ? "primary-inverted" : "light-outline";
    }
    if (colorScheme === BannerGridColorSchemes.PRIMARY_BACKGROUND) {
      return index === 0 ? "light" : "light-outline";
    }
    return index === 0 ? "primary" : "primary-outline";
  };

  return (
    <div
      className={clsx([
        "w-full overflow-hidden relative",
        colorScheme === BannerGridColorSchemes.PRIMARY_BACKGROUND ? "bg-primary" : "bg-neutral",
      ])}
    >
      {backgroundImage?.gatsbyImageData && (
        <>
          <GatsbyImage
            image={backgroundImage?.gatsbyImageData}
            alt={title ?? ""}
            objectFit="cover"
            className="w-full h-full !absolute z-0 top-0 left-0"
          />
          <div className="bg-dark/40 w-full h-full absolute top-0 left-0" />
        </>
      )}
      <div
        className={clsx(
          "w-full px-8 py-5 lg:p-0 flex flex-col lg:flex-row lg:items-center",
          desktopLayout === BannerGridOrientation.IMG_RIGHT && "lg:flex-row-reverse",
          spacing ? "lg:py-11 lg:min-h-160" : "lg:min-h-136"
        )}
      >
        <div className="h-80 lg:h-auto self-stretch lg:flex-[50%] rounded-tr-3xl rounded-bl-3xl overflow-hidden relative">
          <BannerGridImage {...{ image, enableImageZoom, title, imageFit }} />
        </div>
        <div
          className={clsx([
            "lg:flex-[0_0_50%] flex flex-col justify-center gap-8 lg:gap-10 pt-9 pb-12 lg:py-16 lg:px-20 z-10",
          ])}
        >
          {title && (
            <div
              className={clsx(
                "text-4xl 3xl:text-5xl md:text-6xl font-medium",
                !!backgroundImage || colorScheme === BannerGridColorSchemes.PRIMARY_BACKGROUND
                  ? "text-light"
                  : colorScheme === BannerGridColorSchemes.LIGHT_BACKGROUND_PRIMARY_TITLE
                  ? "text-primary"
                  : "text-dark"
              )}
            >
              {title}
            </div>
          )}
          {abstract?.raw && (
            <div
              className={clsx([
                "text-base md:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl leading-snug font-light",
                backgroundImage || colorScheme === BannerGridColorSchemes.PRIMARY_BACKGROUND
                  ? "text-light"
                  : "text-dark",
              ])}
            >
              <RichText raw={abstract.raw} />
            </div>
          )}
          {!!ctas?.length && (
            <div className="flex gap-3 flex-col lg:flex-row items-center">
              {ctas.filter(Boolean).map((cta, index) => (
                <Cta key={index} variant={getCtaVariant(index)} {...cta!} />
              ))}
            </div>
          )}
        </div>
      </div>
      {highlights && (
        <div
          className={clsx(
            "flex items-center w-full px-4",
            spacing ? "lg:-mt-16 mb-5 lg:mb-11" : "lg:-mt-8"
          )}
        >
          <Highlights {...highlights} />
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment BannerGridData on ContentfulBannerGrid {
    __typename
    title
    abstract {
      raw
    }
    image {
      gatsbyImageData
    }
    imageFit
    enableImageZoom
    colorScheme
    backgroundImage {
      gatsbyImageData
    }
    desktopLayout
    spacing
    ctas {
      ...CtaData
    }
    highlights {
      ...HighlightsData
    }
  }
`;
